import api from '../../../services/api';
import { PanelError } from '../../performance-operations/config';
import { toAnalyticsParams } from '../../../components/performance/pmAnalyticsFilters';
import { taskFiltersToParams } from '../../../components/performance/processHealthLinks';
import { formatDate } from '../../../utils/Utils';

// PM Analitik Faz 2 Plan 4 (spec §7.3) — Görevler sekmesi: GET v1/performance-analytics/tasks (ortak süzgeçler + görev süzgeçleri +
// mandatory=true + sayfa + sıralama); Excel aynı uçta export=xlsx (bütün sayfalar). api.js GET'te reject etmez → res.data.status.

// DataTable sütun adı → sunucunun sıralama anahtarı (PmAnalyticsDataAccess.TaskQueryAsync: dueAt|overdueDays|employee|assignee).
export const SORT_KEYS = { employeeName: 'employee', assigneeName: 'assignee', dueAt: 'dueAt', overdueDays: 'overdueDays' };
export const PAGE_SIZE = 20;

export { STAGE_ORDER } from '../../../components/performance/processHealthLinks';

// Hatırlatılabilir görev (PmReminderRules.RemindableStatuses): açık, taslak, iade; sorumlusu bir çalışan (İK havuzu ve harici
// değerlendirici elle hatırlatılmaz, sorumlusuz görevin alıcısı yok).
export const REMINDABLE_STATUSES = ['open', 'draft', 'returned'];
export const canRemind = (row) => !!row && REMINDABLE_STATUSES.includes(row.status) && row.assigneeKind === 'employee' && !!row.assigneeEmployeeId;

export function tasksParams(filters, taskFilters, {
  pageNumber = 1, pageSize = PAGE_SIZE, sort, order,
} = {}) {
  const params = { ...toAnalyticsParams(filters), ...taskFiltersToParams(taskFilters), pageNumber, pageSize };
  if (sort) { params.sort = sort; params.order = order || 'asc'; }
  return params;
}

export async function fetchTasks(params) {
  const res = await api.get('/v1/performance-analytics/tasks', { params });
  if (!res?.data?.status) throw new PanelError(res?.data?.message || 'error');
  const data = res.data.data;
  const rows = Array.isArray(data) ? data : (data?.data || []);
  return { rows, total: Array.isArray(data) ? rows.length : (data?.totalRowCount ?? rows.length) };
}

// Excel indirme (eski PrdPerformanceReportsPage deseni). Başarısız GET'te interceptor başlıksız EMPTY_RESPONSE döner ve hatayı zaten
// tostlamıştır — indirme sessizce terk edilir.
export async function exportTasks(params) {
  // Excel bütün sayfaları döker: sayfa parametreleri düşer.
  const exportParams = { ...params, export: 'xlsx' };
  delete exportParams.pageNumber;
  delete exportParams.pageSize;
  const res = await api.get('/v1/performance-analytics/tasks', { params: exportParams, responseType: 'blob' });
  if (!res?.headers || res.data?.status === false) return false;
  const blob = new Blob([res.data], { type: res.headers['content-type'] });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const match = res.headers['content-disposition']?.match(/filename[^;=\n]*=([^;\n]*)/);
  a.download = match ? match[1].replaceAll('"', '') : 'performance-analytics-tasks.xlsx';
  a.click();
  URL.revokeObjectURL(url);
  return true;
}

// Son aksiyon (spec §7.3): en yeni elle hatırlatma ya da muafiyet, tarihiyle.
export function lastAction(row) {
  const reminder = row?.lastReminderAt ? new Date(row.lastReminderAt).getTime() : null;
  const exempt = row?.exemptAt ? new Date(row.exemptAt).getTime() : null;
  if (reminder == null && exempt == null) return null;
  if (exempt != null && (reminder == null || exempt > reminder)) return { kind: 'exempt', at: row.exemptAt };
  return { kind: 'reminder', at: row.lastReminderAt };
}

export const stageLabel = (t, key) => t(`performance.insight.stage.${key}`, { defaultValue: key });
export const taskLabel = (t, key) => t(`performance.operations.taskKey.${key}`, { defaultValue: key });
export const statusLabel = (t, key) => t(`performance.operations.taskStatus.${key}`, { defaultValue: key });

// E02 yalnız İK'ya açıktır: değerlendirici kimliği gizlenmez; sorumlusu boş çalışan görevi "Atanmamış"tır.
export function assigneeText(t, row) {
  if (row.assigneeKind === 'hr_pool') return t('performance.operations.assignee.hrPool');
  if (row.assigneeKind === 'external') return t('performance.operations.assignee.external');
  if (!row.assigneeEmployeeId) return t('performance.operations.unassigned');
  return row.assigneeName || t('performance.operations.assignee.hidden');
}

// Şeritteki görev süzgeci etiketi (silinebilir). Sorumlu adı çağırandan gelir (kimlikten çözülür).
export function taskChipLabel(t, lang, key, taskFilters, assigneeName) {
  const f = taskFilters;
  const name = t(`performance.processHealth.filter.${key}`);
  switch (key) {
    case 'stage': return `${name}: ${stageLabel(t, f.stage)}`;
    case 'statuses': return `${name}: ${f.statuses.map((s) => statusLabel(t, s)).join(', ')}`;
    case 'overdue': return t(`performance.processHealth.filter.overdueValue.${f.overdue}`);
    case 'assigneeEmployeeId': return assigneeName ? `${name}: ${assigneeName}` : name;
    case 'assigneeKind': return `${name}: ${t(`performance.processHealth.assigneeKind.${f.assigneeKind}`)}`;
    case 'roles': return `${name}: ${f.roles.map((r) => t(`performance.reminder.role.${r}`, { defaultValue: r })).join(', ')}`;
    case 'dueFrom': return `${name}: ${formatDate(f.dueFrom, lang)}`;
    case 'dueTo': return `${name}: ${formatDate(f.dueTo, lang)}`;
    case 'bands': return `${name}: ${f.bands.map((b) => t(`performance.processHealth.band.${b}`)).join(', ')}`;
    case 'dueWithinDays': return t('performance.processHealth.filter.withinValue', { count: f.dueWithinDays });
    default: return name;
  }
}
